import { Box } from '@mui/material';

import { ConfigurationMap, Model, Pie1D, Pie1DData, Project } from '@arra/core';
import { Masonry } from '@mui/lab';
import { Pie, PieChart, ResponsiveContainer } from 'recharts';
import { User } from '../../../authentication/context/useUser';
import { co2Units, humanizeValue } from '../../../actions/page/ActionCard';
import { GraphContainer } from '../../../results/ResultGraph';
import co2 from '../images/Empreinte carbonne.svg';
import { ContentLayout, PageLayout, Text } from '../layout';
import { Footer } from './Footer';
import { Header } from './Header';

export const Co2Details = ({
  project,
  user,
  configuration
}: {
  project: Project;
  user: User;
  configuration: ConfigurationMap | undefined;
}) => {
  if (!project || !user || !configuration) return null;

  const co2Config = configuration['pdfResults.config'].co2Page;
  const detailedResultCode = co2Config.detailedResultCode;

  const co2DetailsResults = project.results
    .filter(
      (result) =>
        result.display === true &&
        result.type === 'pie1D' &&
        result.code !== detailedResultCode,
    )
    .slice(0, 4) as Pie1D[];

  if (co2DetailsResults.length === 0) return null;

  const validResults = co2DetailsResults
    .map((r) => {
      if (!r.data || !r.unit) return null;
      const total = r.data.reduce((acc, val) => acc + val.value, 0);
      if (total === 0) return null;
      return { r, total };
    })
    .filter(Boolean) as { r: Pie1D; total: number }[];

  if (validResults.length === 0) return null;

  return (
    <PageLayout>
      <Header project={project} pageNumber={1} />
      <ContentLayout color="var(--CAMBRIDGEBLUE)">
        <Box display="flex" flexDirection="column" height="100%">
          <Box height="30px" />
          <Box display="flex" columnGap={2} alignItems="center">
            <img src={co2} width="80px" alt="Co2 cloud" />
            <Text fontSize="60px" fontWeight="bold">
              Empreinte carbone
            </Text>
          </Box>
          <Box height="24px" />
          <Box
            height="1px"
            width="100%"
            marginBottom={3}
            sx={{
              background:
                'repeating-linear-gradient(90deg,var(--CAMBRIDGEBLUE) 0 20px,white 0 40px)',
            }}
          />
          <Box flex={1} minHeight={0}>
            <Masonry columns={2} spacing={2}>
              {validResults.map(({ r, total }) => (
                <Box key={r.title} sx={{ breakInside: 'avoid' }}>
                  <Co2Block
                    result={r}
                    total={total}
                    projectModel={project.model}
                  />
                </Box>
              ))}
              {Array.from({ length: Math.max(0, 4 - validResults.length) }).map(
                (_, i) => (
                  <Box key={`empty-${i}`} sx={{ visibility: 'hidden' }}>
                    <Co2Block
                      result={validResults[0].r}
                      total={validResults[0].total}
                      projectModel={project.model}
                    />
                  </Box>
                ),
              )}
            </Masonry>
          </Box>
        </Box>
      </ContentLayout>
      <Footer project={project} user={user} />
    </PageLayout>
  );
};

const Co2Block = ({
  result,
  total,
  projectModel,
}: {
  result: Pie1D;
  total: number;
  projectModel: Model;
}) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      minHeight={0}
      padding={2}
      sx={{
        backgroundColor: 'rgba(255,255,255,0.65)',
        borderRadius: '12px',
      }}
    >
      {/* Title / subtitle */}
      <Box>
        <Text
          fontSize="15px"
          fontWeight="bold"
          color="var(--CAMBRIDGEBLUE)"
          textAlign="left"
        >
          {result.title}
        </Text>
        {result.subtitle && (
          <Box mt={0.5}>
            <Text fontSize="11px" condensed textAlign="left">
              {result.subtitle}
            </Text>
          </Box>
        )}
      </Box>

      {/* Pie */}
      <Box display="flex" justifyContent="center" mt={1} mb={1}>
        <GraphContainer width="100%" height="160px">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={result.data}
                cx={'50%'}
                cy={'50%'}
                innerRadius="0%"
                outerRadius="100%"
                dataKey="value"
                isAnimationActive={false}
              />
            </PieChart>
          </ResponsiveContainer>
        </GraphContainer>
      </Box>

      {/* Legend */}
      <Box mt={1} display="flex" flexDirection="column" rowGap={1}>
        <Pie1DPDFLegend data={result.data!} unit={result.unit!} total={total} />
      </Box>
    </Box>
  );
};

interface Pie1DPDFLegendProps {
  data: Pie1DData[];
  unit: string;
  total: number;
}

const Pie1DPDFLegend = ({ data, unit, total }: Pie1DPDFLegendProps) => {
  const impactScope = co2Units.has(unit) ? 'co2' : '';

  return (
    <Box display="flex" flexDirection="column" rowGap={1}>
      {data
        .sort((cat1, cat2) => cat2.value - cat1.value)
        .map((category) => {
          if (category.value === 0) return null;
          const percent = Math.round((category.value / total) * 100);
          const humanizedCategoryValue = humanizeValue(
            unit,
            Number(category.value),
            impactScope,
          );

          return (
            <Box
              key={category.name}
              display="flex"
              alignItems="center"
              columnGap={1.5}
            >
              <Box
                width="12px"
                height="12px"
                borderRadius="50%"
                bgcolor={category.fill}
                flexShrink={0}
              />
              <p style={{ fontSize: '11px' }}>
                <b>{category.name}</b> :&nbsp;
                {humanizedCategoryValue.value}&nbsp;
                {humanizedCategoryValue.unit}, soit {percent}%
              </p>
            </Box>
          );
        })}
    </Box>
  );
};
